import {useEffect} from 'react';
import {UseFormSetValue, UseFormGetValues} from 'react-hook-form';
import {useQueryClient} from '@tanstack/react-query';
import {FormValues} from '../schema';
import {IIngestionCopyResponse} from '../../../../api/types/ingestionApi.type';
import {prefillFormWithLoadedData} from '../utils/formPrefillUtils';
import {prefillCopyFlowDropdowns} from '../utils/copyFlowPrefillUtils';

type DropdownOption = {key: string; displayValue: string};

interface UseIngestionFormEffectsParams {
  // Form methods
  setValue: UseFormSetValue<FormValues>;
  getValues: UseFormGetValues<FormValues>;

  // Watched values
  selectedServerLocation: DropdownOption | null;
  selectedFolder: DropdownOption | null;
  fieldSeparatorValue: DropdownOption | null;
  textQualifierValue: DropdownOption | null;
  dataDelimiterValue: DropdownOption | null;
  datLoadFile: DropdownOption | null;

  // Dropdown data
  columnDelimeter: DropdownOption[];
  dataDelimeter: DropdownOption[];
  textQualifier: DropdownOption[];
  timeZone: DropdownOption[];
  dateFormat: DropdownOption[];
  sourceSystem: DropdownOption[];
  serverLocation: DropdownOption[];
  sourceFolderOptions: DropdownOption[];
  loadFiles: DropdownOption[];
  headersDocData: DropdownOption[];
  linkType: DropdownOption[];
  rawSourceFolders: any;

  // Wizard data
  wizardData: any;
  isLoading: boolean;
  datFileValue: string | null;

  // Flow control
  isCopyFlow: boolean;
  isEditFlow: boolean;
  loadedData: IIngestionCopyResponse | null;
  dataLoaded: boolean;

  // State setters
  setFolderAccessError: (error: boolean) => void;
  editDataPrefilled: boolean;
  setEditDataPrefilled: (value: boolean) => void;
  folderPrefilled: boolean;
  setFolderPrefilled: (value: boolean) => void;
  sourcesPrefilled: boolean;
  setSourcesPrefilled: (value: boolean) => void;
  lastProcessedFolder: string | null;
  setLastProcessedFolder: (value: string | null) => void;
}

export const useIngestionFormEffects = (
  params: UseIngestionFormEffectsParams
) => {
  const {
    setValue,
    getValues,
    selectedServerLocation,
    selectedFolder,
    fieldSeparatorValue,
    textQualifierValue,
    dataDelimiterValue,
    columnDelimeter,
    dataDelimeter,
    datLoadFile,
    textQualifier,
    timeZone,
    dateFormat,
    sourceSystem,
    serverLocation,
    sourceFolderOptions,
    loadFiles,
    headersDocData,
    rawSourceFolders,
    wizardData,
    linkType,
    isLoading,
    datFileValue,
    isCopyFlow,
    isEditFlow,
    loadedData,
    dataLoaded,
    setFolderAccessError,
    editDataPrefilled,
    setEditDataPrefilled,
    folderPrefilled,
    setFolderPrefilled,
    sourcesPrefilled,
    setSourcesPrefilled,
    lastProcessedFolder,
    setLastProcessedFolder,
  } = params;

  const queryClient = useQueryClient();

  // 1. Set delimiter defaults from wizard API (ONLY for pure wizard flow)
  useEffect(() => {
    if (isLoading || !wizardData) return;
    if (isCopyFlow || isEditFlow) return;

    // Field Separator
    if (
      wizardData.selectedColumnDelimiterValue !== undefined &&
      columnDelimeter.length > 0
    ) {
      const current = getValues('fieldSeparator');
      if (current == null) {
        const defaultValue = columnDelimeter.find(
          (item) => Number(item.key) === wizardData.selectedColumnDelimiterValue
        );
        if (defaultValue) {
          setValue('fieldSeparator', defaultValue, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }
      }
    }

    // Text Qualifier
    if (
      wizardData.selectedTextDelimiterValue !== undefined &&
      textQualifier.length > 0
    ) {
      const current = getValues('textQualifier');
      if (current == null) {
        const defaultValue = textQualifier.find(
          (item) => Number(item.key) === wizardData.selectedTextDelimiterValue
        );
        if (defaultValue) {
          setValue('textQualifier', defaultValue, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }
      }
    }

    // Multi-Value (Data Delimiter)
    if (
      wizardData.selectedDataDelimiterValue !== undefined &&
      dataDelimeter.length > 0
    ) {
      const current = getValues('multiValue');
      if (current == null) {
        const defaultValue = dataDelimeter.find(
          (item) => Number(item.key) === wizardData.selectedDataDelimiterValue
        );
        if (defaultValue) {
          setValue('multiValue', defaultValue, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }
      }
    }
  }, [
    isLoading,
    wizardData,
    columnDelimeter,
    dataDelimeter,
    textQualifier,
    getValues,
    setValue,
    isCopyFlow,
    isEditFlow,
  ]);

  // 2.  Set other defaults (timeZone, ingestionType)
  useEffect(() => {
    if (!isLoading && wizardData) {
      if (timeZone.length > 0) {
        const current = getValues('timeZone');
        if (!current) {
          setValue('timeZone', timeZone[0]);
        }
      }
    }
  }, [isLoading, wizardData, timeZone, getValues, setValue]);

  // 3. Check folder access
  useEffect(() => {
    if (selectedServerLocation && rawSourceFolders !== undefined) {
      setFolderAccessError(!rawSourceFolders || rawSourceFolders.length === 0);
    }
  }, [selectedServerLocation, rawSourceFolders, setFolderAccessError]);

  // 4. Reset folder on server change (ONLY for wizard flow)
  useEffect(() => {
    if (isEditFlow || isCopyFlow) return;
    setValue('sourceFolder', null);
  }, [selectedServerLocation, setValue, isEditFlow, isCopyFlow]);
  // 5. ✅ UPDATED: Reset DAT selections when folder changes (ALL FLOWS except Overlay Edit with isDATFolder=false)
  useEffect(() => {
    const folder = selectedFolder;

    if (!folder) return;
    if (loadFiles.length === 0) return;
    if (headersDocData.length === 0) return;

    const currentFolderKey = folder.key;
    if (currentFolderKey === lastProcessedFolder) return;

    // ✅ NEW: Skip DAT auto-enable for Edit flow with Overlay type when isDATFolder is false
    if (isEditFlow && loadedData) {
      const ingestionType = getValues('ingestionType');
      const isDATFolder =
        loadedData.ingestionWizardModel.ingestionSpecifySetting.isDATFolder;
      const isOverlay =
        ingestionType?.key === '2' || ingestionType?.key === 'Overlay' ||
        ingestionType?.displayValue?.toLowerCase() === 'overlay';

      if (isOverlay && !isDATFolder) {
        // Don't auto-enable DAT checkbox
        setLastProcessedFolder(currentFolderKey);
        return;
      }
    }

    const sourcesState = getValues('sources');
    const updatedSources = {...sourcesState};

    // 1. Enable DAT checkbox
    if (!updatedSources.dat.enabled) {
      updatedSources.dat.enabled = true;
    }

    // 2. ✅ RESET: Set DAT Load File based on API's loadDAT value
    const suggestedLoadFile = datFileValue
      ? loadFiles.find(
        (file) =>
          file.key === datFileValue || file.displayValue === datFileValue
      )
      : null;

    const fileToSet = suggestedLoadFile || loadFiles[0];
    updatedSources.dat.loadFile = fileToSet;

    // 3. ✅ RESET: Clear Document Key (force user to re-select)
    updatedSources.dat.documentKey = null;

    // Update form state
    setValue('sources', updatedSources, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Mark this folder as processed
    setLastProcessedFolder(currentFolderKey);
  }, [
    selectedFolder,
    loadFiles,
    headersDocData,
    datFileValue,
    getValues,
    setValue,
    lastProcessedFolder,
    setLastProcessedFolder,
    isEditFlow,
    loadedData,
  ]);

  // 6. Reset lastProcessedFolder when server location changes
  useEffect(() => {
    setLastProcessedFolder(null);
  }, [selectedServerLocation?.key, setLastProcessedFolder]);

  // 7. Invalidate queries when delimiters change
  useEffect(() => {
    if (
      selectedFolder &&
      fieldSeparatorValue?.key &&
      textQualifierValue?.key &&
      dataDelimiterValue?.key
    ) {
      queryClient.invalidateQueries({
        queryKey: [
          'ingestion',
          'sourceFoldersData',
          selectedServerLocation?.key ?? null,
          selectedFolder?.key ?? null,
        ],
      });

      if (datFileValue) {
        queryClient.invalidateQueries({
          queryKey: [
            'ingestion',
            'sourceHeaders',
            selectedServerLocation?.key ?? null,
            selectedFolder?.key ?? null,
            fieldSeparatorValue?.key ?? null,
            textQualifierValue?.key ?? null,
            dataDelimiterValue?.key ?? null,
            datFileValue ?? null,
          ],
        });
      }
    }
  }, [
    fieldSeparatorValue?.key,
    textQualifierValue?.key,
    dataDelimiterValue?.key,
    selectedFolder?.key,
    selectedServerLocation?.key,
    datFileValue,
    queryClient,
  ]);

  // 7.5. ✅ NEW: Trigger headers API when DAT Load File changes
  useEffect(() => {
    if (
      selectedFolder &&
      fieldSeparatorValue?.key &&
      textQualifierValue?.key &&
      dataDelimiterValue?.key &&
      datLoadFile?.key
    ) {
      // Invalidate headers query to refetch with new DAT file
      queryClient.invalidateQueries({
        queryKey: [
          'ingestion',
          'sourceHeaders',
          selectedServerLocation?.key ?? null,
          selectedFolder?.key ?? null,
          fieldSeparatorValue?.key ?? null,
          textQualifierValue?.key ?? null,
          dataDelimiterValue?.key ?? null,
          datLoadFile.key, // ✅ Use the selected DAT file
        ],
      });
    }
  }, [
    datLoadFile?.key, // ✅ Watch DAT Load File changes
    selectedFolder?.key,
    selectedServerLocation?.key,
    fieldSeparatorValue?.key,
    textQualifierValue?.key,
    dataDelimiterValue?.key,
    queryClient,
  ]);

  // 8. Phase 1: Prefill basic dropdowns (Copy/Edit flows)
  useEffect(() => {
    if (!loadedData || !dataLoaded || isLoading) return;

    if (isCopyFlow) {
      prefillCopyFlowDropdowns(loadedData, setValue, {
        sourceSystem,
        serverLocation,
        columnDelimeter,
        dataDelimeter,
        textQualifier,
        dateFormat,
      });
    } else if (isEditFlow) {
      prefillFormWithLoadedData(loadedData, setValue, {
        sourceSystem,
        serverLocation,
        columnDelimeter,
        dataDelimeter,
        textQualifier,
        dateFormat,
        timeZone,
        sourceFolderOptions: [],
      });
      setEditDataPrefilled(true);
    }
  }, [
    loadedData,
    dataLoaded,
    isLoading,
    isCopyFlow,
    isEditFlow,
    setValue,
    sourceSystem,
    serverLocation,
    columnDelimeter,
    dataDelimeter,
    textQualifier,
    dateFormat,
    timeZone,
    setEditDataPrefilled,
  ]);

  // 9.  Phase 2: Prefill source folder (EDIT FLOW ONLY)
  useEffect(() => {
    if (!isEditFlow || !editDataPrefilled || !loadedData) return;
    if (folderPrefilled) return;
    if (sourceFolderOptions.length === 0) {
      return;
    }

    const apiDropdowns = loadedData.fillDropDownListFromSessionResult.result;

    if (apiDropdowns.FolderName) {
      const selectedValue = apiDropdowns.FolderName[0]?.selected;
      if (!selectedValue) return;

      const selectedApiOption = apiDropdowns.FolderName.find(
        (opt) => String(opt.value) === String(selectedValue)
      );

      if (!selectedApiOption) {
        return;
      }

      const matchedWizardOption = sourceFolderOptions.find(
        (opt) => opt.displayValue === selectedApiOption.key
      );

      if (matchedWizardOption) {
        setValue('sourceFolder', matchedWizardOption, {shouldDirty: false});
        setFolderPrefilled(true);
      }
    }
  }, [
    isEditFlow,
    editDataPrefilled,
    folderPrefilled,
    sourceFolderOptions,
    loadedData,
    setValue,
    setFolderPrefilled,
  ]);

  // 10. Phase 3: Prefill ALL sources (EDIT FLOW ONLY)
  // 10. ✅ UPDATED: Phase 3: Prefill ALL sources using "selected: true" (EDIT FLOW ONLY)
  useEffect(() => {
    if (!isEditFlow || !editDataPrefilled || !loadedData) return;
    if (sourcesPrefilled) return;
    if (loadFiles.length === 0 || headersDocData.length === 0) {
      return;
    }

    const settings = loadedData.ingestionWizardModel.ingestionSpecifySetting;
    const apiDropdowns = loadedData.fillDropDownListFromSessionResult.result;

    const currentSources = getValues('sources');
    const updatedSources = {...currentSources};

    /**
     * ✅ NEW: Helper to find selected option from Edit API
     * Looks for {selected: true} or {selected: 'true'} in the API response
     */
    const findSelectedFromEditAPI = (
      apiOptions:
        | Array<{
          key: string;
          value: string;
          selected: boolean | string;
          text: string | null;
        }>
        | undefined
    ): string | null => {
      if (!apiOptions || apiOptions.length === 0) return null;

      const selectedOption = apiOptions.find(
        (opt) =>
          opt.selected === true ||
          opt.selected === 'true' ||
          String(opt.selected).toLowerCase() === 'true'
      );

      return selectedOption?.value || null;
    };

    /**
     * ✅ NEW: Match Edit API value with wizard dropdown options
     */
    const matchWithWizardDropdown = (
      apiValue: string | null,
      wizardOptions: DropdownOption[]
    ): DropdownOption | null => {
      if (!apiValue) return null;

      return (
        wizardOptions.find(
          (opt) => opt.key === apiValue || opt.displayValue === apiValue
        ) || null
      );
    };

    // ==================== DAT ====================
    if (settings.isDATFolder) {
      updatedSources.dat.enabled = true;

      // DAT Load File
      const selectedDATLoadFile = findSelectedFromEditAPI(
        apiDropdowns.DATLoadFiles
      );
      if (selectedDATLoadFile) {
        const matched = matchWithWizardDropdown(selectedDATLoadFile, loadFiles);
        if (matched) {
          updatedSources.dat.loadFile = matched;
        }
      }

      // DAT Document Key
      const selectedDATDocKey = findSelectedFromEditAPI(
        apiDropdowns.DATKeyColumn
      );
      if (selectedDATDocKey) {
        const matched = matchWithWizardDropdown(
          selectedDATDocKey,
          headersDocData
        );
        if (matched) {
          updatedSources.dat.documentKey = matched;
        }
      }
    } else {
      // ✅ IMPORTANT: Explicitly set DAT to disabled if API says it wasn't enabled
      updatedSources.dat.enabled = false;
      updatedSources.dat.loadFile = null;
      updatedSources.dat.documentKey = null;
    }

    // ==================== NATIVE ====================
    if (settings.isNativeFolder) {
      updatedSources.native.enabled = true;
      updatedSources.native.isPathInDatFile = settings.isDATNative;

      // Native Load File
      const selectedNativeLoadFile = findSelectedFromEditAPI(
        apiDropdowns.NativeLoadFiles
      );
      if (selectedNativeLoadFile && selectedNativeLoadFile !== '0') {
        const matched = matchWithWizardDropdown(
          selectedNativeLoadFile,
          loadFiles
        );
        if (matched) {
          updatedSources.native.loadFile = matched;
        }
      }

      // Native File Path Field
      if (settings.isDATNative) {
        const selectedNativeFilePath = findSelectedFromEditAPI(
          apiDropdowns.NativeFilePath
        );
        if (selectedNativeFilePath && selectedNativeFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedNativeFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.native.filePathField = matched;
          }
        }
      }
    }

    // ==================== TEXT ====================
    if (settings.isTextFolder) {
      updatedSources.text.enabled = true;
      updatedSources.text.isPathInDatFile = settings.isDATText;

      // Text Load File
      const selectedTextLoadFile = findSelectedFromEditAPI(
        apiDropdowns.TextLoadFiles
      );
      if (selectedTextLoadFile && selectedTextLoadFile !== '0') {
        const matched = matchWithWizardDropdown(
          selectedTextLoadFile,
          loadFiles
        );
        if (matched) {
          updatedSources.text.loadFile = matched;
        }
      }

      // Text File Path Field
      if (settings.isDATText) {
        const selectedTextFilePath = findSelectedFromEditAPI(
          apiDropdowns.TextFilePath
        );
        if (selectedTextFilePath && selectedTextFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedTextFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.text.filePathField = matched;
          }
        }
      }
    }

    // ==================== PDF ====================
    if (settings.isPDFFolder) {
      updatedSources.pdf.enabled = true;
      updatedSources.pdf.isPathInDatFile = settings.isDATPDF;

      // PDF Load File
      const selectedPDFLoadFile = findSelectedFromEditAPI(
        apiDropdowns.PDFLoadFiles
      );
      if (selectedPDFLoadFile && selectedPDFLoadFile !== '0') {
        const matched = matchWithWizardDropdown(selectedPDFLoadFile, loadFiles);
        if (matched) {
          updatedSources.pdf.loadFile = matched;
        }
      }

      // PDF File Path Field
      if (settings.isDATPDF) {
        const selectedPDFFilePath = findSelectedFromEditAPI(
          apiDropdowns.PDFFilePath
        );
        if (selectedPDFFilePath && selectedPDFFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedPDFFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.pdf.filePathField = matched;
          }
        }
      }
    }

    // ==================== TIFF ====================
    if (settings.isTIFFFolder) {
      updatedSources.tiff.enabled = true;
      updatedSources.tiff.isPathInDatFile = settings.isDATTIFF;
      updatedSources.tiff.generateSearchablePdfs =
        settings.isGenerateSearchablePDFforTIFFs;

      // TIFF Load File
      const selectedTIFFLoadFile = findSelectedFromEditAPI(
        apiDropdowns.TIFFLoadFiles
      );
      if (selectedTIFFLoadFile && selectedTIFFLoadFile !== '0') {
        const matched = matchWithWizardDropdown(
          selectedTIFFLoadFile,
          loadFiles
        );
        if (matched) {
          updatedSources.tiff.loadFile = matched;
        }
      }

      // TIFF File Path Field
      if (settings.isDATTIFF) {
        const selectedTIFFFilePath = findSelectedFromEditAPI(
          apiDropdowns.TIFFFilePath
        );
        if (selectedTIFFFilePath && selectedTIFFFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedTIFFFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.tiff.filePathField = matched;
          }
        }
      }
    }

    // ==================== MP3 ====================
    if (settings.isMP3VariantFolder) {
      updatedSources.mp3.enabled = true;
      updatedSources.mp3.isPathInDatFile = settings.isDATMP3Variant;

      // MP3 Load File
      const selectedMP3LoadFile = findSelectedFromEditAPI(
        apiDropdowns.MP3VariantLoadFiles
      );
      if (selectedMP3LoadFile && selectedMP3LoadFile !== '0') {
        const matched = matchWithWizardDropdown(selectedMP3LoadFile, loadFiles);
        if (matched) {
          updatedSources.mp3.loadFile = matched;
        }
      }

      // MP3 File Path Field
      if (settings.isDATMP3Variant) {
        const selectedMP3FilePath = findSelectedFromEditAPI(
          apiDropdowns.MP3VariantFilePath
        );
        if (selectedMP3FilePath && selectedMP3FilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedMP3FilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.mp3.filePathField = matched;
          }
        }
      }
    }

    // ==================== AUDIO TRANSCRIPT ====================
    if (settings.isTranscriptFolder) {
      updatedSources.audio.enabled = true;
      updatedSources.audio.isPathInDatFile = settings.isDATTranscript;

      // Audio Transcript Load File
      const selectedAudioLoadFile = findSelectedFromEditAPI(
        apiDropdowns.TranscriptLoadFiles
      );
      if (selectedAudioLoadFile && selectedAudioLoadFile !== '0') {
        const matched = matchWithWizardDropdown(
          selectedAudioLoadFile,
          loadFiles
        );
        if (matched) {
          updatedSources.audio.loadFile = matched;
        }
      }

      // Audio Transcript File Path Field
      if (settings.isDATTranscript) {
        const selectedAudioFilePath = findSelectedFromEditAPI(
          apiDropdowns.TranscriptFilePath
        );
        if (selectedAudioFilePath && selectedAudioFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedAudioFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.audio.filePathField = matched;
          }
        }
      }
    }

    // ==================== OTHER ====================
    if (settings.isOtherFolder) {
      updatedSources.other.enabled = true;
      updatedSources.other.isPathInDatFile = settings.isDATOther;

      // Link Type
      const selected = apiDropdowns.LinkType.find(opt => opt.selected);
      if (selected) {
        updatedSources.other.linkType =
          linkType.find(opt => opt.key === selected.key) || null;
      }

      // Other Load File
      const selectedOtherLoadFile = findSelectedFromEditAPI(
        apiDropdowns.OtherLoadFiles
      );
      if (selectedOtherLoadFile && selectedOtherLoadFile !== '0') {
        const matched = matchWithWizardDropdown(
          selectedOtherLoadFile,
          loadFiles
        );
        if (matched) {
          updatedSources.other.loadFile = matched;
        }
      }

      // Other File Path Field
      if (settings.isDATOther) {
        const selectedOtherFilePath = findSelectedFromEditAPI(
          apiDropdowns.OtherFilePath
        );
        if (selectedOtherFilePath && selectedOtherFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedOtherFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.other.filePathField = matched;
          }
        }
      }
    }
    
    // Build annotation layer lookup list from wizard data.
    // API items: {key, value (label), selected, text: null} — label is in `value`.
    const annotationLayerOptions: DropdownOption[] = (() => {
      return (apiDropdowns?.AnnotationLayers ?? [])
        .map((item: any) => ({
          key: String(item.key ?? ''),
          displayValue: item.value ?? item.text ?? '',
        }))
    })();

    if (settings.isRNFVFolder) {
      updatedSources.redactedNative.enabled = true;
      updatedSources.redactedNative.isPathInDatFile = settings.isDATRNFV;

      // Redacted Native Load File
      const selectedRNFVLoadFile = findSelectedFromEditAPI(
        apiDropdowns.RNFVLoadFiles
      );
      if (selectedRNFVLoadFile && selectedRNFVLoadFile !== '0') {
        const matched = matchWithWizardDropdown(selectedRNFVLoadFile, loadFiles);
        if (matched) {
          updatedSources.redactedNative.loadFile = matched;
        }
      }

      // Redacted Text Load File
      const selectedRNFVTextLoadFile = findSelectedFromEditAPI(
        apiDropdowns.RNFVTXTLoadFiles
      );
      if (selectedRNFVTextLoadFile && selectedRNFVTextLoadFile !== '0') {
        const matched = matchWithWizardDropdown(selectedRNFVTextLoadFile, loadFiles);
        if (matched) {
          updatedSources.redactedNative.redactedTextLoadFile = matched;
        }
      }

      // Annotation Layer
      const selectedAnnotationLayer = findSelectedFromEditAPI(
        apiDropdowns.AnnotationLayers
      );
      if (selectedAnnotationLayer && selectedAnnotationLayer !== '0') {
        const matched = matchWithWizardDropdown(
          selectedAnnotationLayer,
          annotationLayerOptions
        );
        updatedSources.redactedNative.annotationLayer = '1';
        console.log('Matching annotation layer:', {selectedAnnotationLayer, matched});
        if (matched) {
          updatedSources.redactedNative.annotationLayer = matched;
        }
      }

      // File Path Fields in DAT (only when isPathInDatFile is true)
      if (settings.isDATRNFV) {
        const selectedRNFVFilePath = findSelectedFromEditAPI(
          apiDropdowns.RNFVFilePath
        );
        if (selectedRNFVFilePath && selectedRNFVFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedRNFVFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.redactedNative.filePathField = matched;
          }
        }

        const selectedRNFVTextFilePath = findSelectedFromEditAPI(
          apiDropdowns.RNFVTXTFilePath
        );
        if (selectedRNFVTextFilePath && selectedRNFVTextFilePath !== '0') {
          const matched = matchWithWizardDropdown(
            selectedRNFVTextFilePath,
            headersDocData
          );
          if (matched) {
            updatedSources.redactedNative.redactedTextFilePathField = matched;
          }
        }
      }
    } else {
      updatedSources.redactedNative.enabled = false;
      updatedSources.redactedNative.loadFile = null;
      updatedSources.redactedNative.redactedTextLoadFile = null;
      updatedSources.redactedNative.annotationLayer = null;
      updatedSources.redactedNative.isPathInDatFile = false;
      updatedSources.redactedNative.filePathField = null;
      updatedSources.redactedNative.redactedTextFilePathField = null;
    }

    setValue('sources', updatedSources, {shouldDirty: false});
    setSourcesPrefilled(true);

    if (selectedFolder) {
      setLastProcessedFolder(selectedFolder.key);
    }
  }, [
    isEditFlow,
    editDataPrefilled,
    sourcesPrefilled,
    loadFiles,
    headersDocData,
    loadedData,
    setValue,
    getValues,
    selectedFolder,
    setSourcesPrefilled,
    setLastProcessedFolder,
  ]);
};
